/**
 * Aldwyn Discount System v4 - Hands-Free Preview + Checkout Apply
 *
 * ARCHITECTURE:
 * 1. Cart Drawer: Show discount PREVIEW only (instant, no API)
 * 2. Checkout Click: Apply discount via API (single call)
 * 3. Auto Recovery: Refund points if checkout abandoned
 *
 * FIX (v4.1): Stale discount upgrade — if a customer's AD balance has grown
 * since a discount code was created, the old code is silently removed so
 * checkout will create a new, correctly-sized code.
 */
(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════

  const CONFIG = {
    API_BASE: (window.ALDWYN && window.ALDWYN.vercel_api_base) || 'https://server-vercel-aldwyn.vercel.app/api/aldwyn',
    POINTS_PER_DOLLAR: 20,
    MIN_DISCOUNT: 20,
    DISCOUNT_EXPIRY: 30 * 60 * 1000
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════

  let state = {
    isApplying: false,
    isRemoving: false,
    previewPoints: 0,
    appliedCode: null
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STORAGE
  // ═══════════════════════════════════════════════════════════════════════════

  async function getStoredDiscount() {
    const justRemoved = localStorage.getItem('aldwyn_just_removed');
    if (justRemoved) {
      const timeSince = Date.now() - parseInt(justRemoved, 10);
      if (timeSince < 5000) {
        log('Discount was just removed, ignoring API check for ' + (5000 - timeSince) + 'ms');
        return null;
      } else {
        localStorage.removeItem('aldwyn_just_removed');
      }
    }

    const apiDiscount = await apiCheckActiveDiscount();
    if (apiDiscount) {
      saveDiscount(apiDiscount.code, apiDiscount.points);
      return apiDiscount;
    }

    log('API reports no active discount - clearing stale localStorage');
    clearStoredDiscount();
    localStorage.removeItem('aldwyn_checkout_code');
    localStorage.removeItem('aldwyn_checkout_time');
    localStorage.removeItem('aldwyn_checkout_points');

    return null;
  }

  function saveDiscount(code, points) {
    localStorage.setItem('aldwyn_applied_code', code);
    localStorage.setItem('aldwyn_applied_data', JSON.stringify({ points, timestamp: Date.now() }));
  }

  function clearStoredDiscount() {
    localStorage.removeItem('aldwyn_applied_code');
    localStorage.removeItem('aldwyn_applied_data');
  }

  function getCustomer() {
    if (!window.ALDWYN) { log('getCustomer: window.ALDWYN not found'); return null; }
    const email = window.ALDWYN.customer_email;
    const id = window.ALDWYN.customer_id;
    if (!email || !id) { log('getCustomer: Missing email or id', { email, id }); return null; }
    return { email, id, balance: window.ALDWYN.balance || 0 };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MONEY HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  function centsToPoints(cents) { return Math.floor(cents / 5); }
  function pointsToCents(points) { return points * 5; }
  function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }
  function log(msg, data = '') { console.log('[Aldwyn v4] ' + msg, data); }

  // ═══════════════════════════════════════════════════════════════════════════
  // NOTIFICATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  function showNotification(type, message, duration = 3000) {
    document.getElementById('aldwyn-notif')?.remove();
    const icons = { working: '·', success: '✓', error: '✕', info: '·' };
    const el = document.createElement('div');
    el.id = 'aldwyn-notif';
    el.innerHTML = '<span style="margin-right:8px;font-size:16px;">' + (icons[type] || '·') + '</span>' + message;
    el.style.cssText = 'position:fixed;right:16px;bottom:72px;z-index:99999;background:#37352F;border:0.5px solid #37352F;color:#fff;padding:10px 16px;border-radius:0;font-weight:600;font-size:11px;letter-spacing:0.08em;text-transform:uppercase;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);display:flex;align-items:center;animation:aldwyn-in 0.3s ease-out;';

    if (!document.getElementById('aldwyn-styles')) {
      const style = document.createElement('style');
      style.id = 'aldwyn-styles';
      style.textContent = '@keyframes aldwyn-in{from{transform:translateX(120%);opacity:0}to{transform:translateX(0);opacity:1}}@keyframes aldwyn-out{from{transform:translateX(0);opacity:1}to{transform:translateX(120%);opacity:0}}';
      document.head.appendChild(style);
    }

    document.body.appendChild(el);

    if (duration > 0) {
      setTimeout(() => {
        el.style.animation = 'aldwyn-out 0.3s ease-in forwards';
        setTimeout(() => el.remove(), 300);
      }, duration);
    }

    return el;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CART ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  function getEligibleTotal() {
    const items = document.querySelectorAll('[data-product-tags]');
    let eligibleCents = 0;
    items.forEach(item => {
      const tags = item.dataset.productTags || '';
      if (tags.toLowerCase().includes('nexchange')) {
        const priceStr = item.dataset.originalLinePrice || '0';
        eligibleCents += parseInt(priceStr, 10) || 0;
      }
    });
    return eligibleCents;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PREVIEW CALCULATION
  // ═══════════════════════════════════════════════════════════════════════════

  function calculatePreview() {
    const customer = getCustomer();
    if (!customer) return { points: 0, dollars: 0, eligible: 0 };

    const eligibleCents = getEligibleTotal();
    if (eligibleCents === 0) return { points: 0, dollars: 0, eligible: 0 };

    const maxPoints = Math.min(customer.balance, centsToPoints(eligibleCents));
    const points = maxPoints >= CONFIG.MIN_DISCOUNT ? maxPoints : 0;
    const dollars = pointsToCents(points) / 100;

    return { points, dollars, eligible: eligibleCents / 100 };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UI UPDATES
  // ═══════════════════════════════════════════════════════════════════════════

  async function updateCartUI() {
    const customer = getCustomer();
    log('updateCartUI called, customer:', customer ? 'logged in (' + customer.email + ')' : 'not logged in');

    const cartEmpty = document.querySelector('.drawer__inner-empty');

    // --- Alert container ---
    let alertContainer = document.getElementById('aldwyn-alert-container');

    if (cartEmpty) {
      log('Cart is empty');
      if (!alertContainer) {
        const emptyContent = document.querySelector('.cart-drawer__empty-content');
        if (emptyContent) {
          alertContainer = document.createElement('div');
          alertContainer.id = 'aldwyn-alert-container';
          alertContainer.style.cssText = 'margin:1rem 0;';
          const continueBtn = emptyContent.querySelector('a.button');
          if (continueBtn && continueBtn.nextSibling) {
            emptyContent.insertBefore(alertContainer, continueBtn.nextSibling);
          } else if (emptyContent.lastChild) {
            emptyContent.appendChild(alertContainer);
          }
        }
      }

      if (!customer) {
        if (alertContainer) {
          alertContainer.innerHTML = '<div style="padding:14px 18px;background:#EAEBEA;border-top:0.5px solid #E0DDD8;text-align:center;margin-top:12px;"><span style="display:block;font-size:10px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;color:#9BB8B1;margin-bottom:6px;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">Aldwyn Dollars</span><p style="color:#777;font-size:13px;font-weight:400;margin:0;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);"><a href="/account/login" style="color:#37352F;font-weight:600;text-decoration:underline;">Log in</a> to redeem your balance</p></div>';
        }
      } else {
        if (alertContainer) alertContainer.innerHTML = '';
      }

      document.getElementById('aldwyn-status-container')?.remove();
      return;
    }

    // --- Non-empty cart: alert container in drawer footer ---
    if (!alertContainer) {
      const drawerFooter = document.querySelector('.drawer__footer');
      const cartDrawerFooter = document.querySelector('.cart-drawer__footer');
      if (drawerFooter && cartDrawerFooter && drawerFooter.contains(cartDrawerFooter)) {
        alertContainer = document.createElement('div');
        alertContainer.id = 'aldwyn-alert-container';
        alertContainer.style.cssText = 'padding:0 1.5rem;margin:0 0 1rem 0;';
        try {
          drawerFooter.insertBefore(alertContainer, drawerFooter.firstChild);
        } catch (e) {
          log('Failed to insert alert container:', e.message);
          alertContainer = null;
        }
      }
    }

    // --- Status container (inside balance box) ---
    let statusContainer = document.getElementById('aldwyn-status-container');
    if (!statusContainer) {
      const balanceBox = document.getElementById('aldwyn-cart-balance');
      if (balanceBox) {
        statusContainer = document.createElement('div');
        statusContainer.id = 'aldwyn-status-container';
        balanceBox.appendChild(statusContainer);
      }
    }

    // --- Non-logged-in ---
    if (!customer) {
      if (alertContainer) {
        alertContainer.innerHTML = '<div style="padding:14px 18px;background:#EAEBEA;border-top:0.5px solid #E0DDD8;text-align:center;"><span style="display:block;font-size:10px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;color:#9BB8B1;margin-bottom:6px;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">Aldwyn Dollars</span><p style="color:#777;font-size:13px;font-weight:400;margin:0;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);"><a href="/account/login" style="color:#37352F;font-weight:600;text-decoration:underline;">Log in</a> to redeem your balance</p></div>';
      }
      if (statusContainer) statusContainer.innerHTML = '';
      return;
    }

    // --- Logged-in ---
    log('Logged-in user, checking for active discounts...');
    const storedDiscount = await getStoredDiscount();

    // Update balance display
    const balanceEl = document.getElementById('CartDrawer-AldwynBalance');
    const usdEl = document.getElementById('CartDrawer-AldwynUSD');
    if (balanceEl) balanceEl.textContent = customer.balance.toLocaleString();
    if (usdEl) usdEl.textContent = (customer.balance / CONFIG.POINTS_PER_DOLLAR).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const eligibleCents = getEligibleTotal();
    const hasEligibleItems = eligibleCents > 0;

    let preview;
    if (storedDiscount) {
      preview = { points: storedDiscount.points, dollars: pointsToCents(storedDiscount.points) / 100, eligible: eligibleCents / 100 };
    } else {
      preview = calculatePreview();
    }
    state.previewPoints = preview.points;

    if (alertContainer) alertContainer.innerHTML = '';

    // Auto-refund if stored discount exists but no eligible items
    if (storedDiscount && !hasEligibleItems) {
      log('Stored discount with no eligible items — auto-refunding');
      await handleRemoveDiscount(storedDiscount.code, true);
      if (statusContainer) statusContainer.innerHTML = '';
      clearPreviewFromCart();
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // STALE DISCOUNT FIX
    // If a discount code was created in a previous session but the customer's
    // AD balance has since grown (e.g. they completed another exchange), the
    // stored code is for a smaller amount than they're now entitled to.
    // Silently remove it so checkout creates a fresh, correctly-sized code.
    // ─────────────────────────────────────────────────────────────────────────
    if (storedDiscount) {
      const freshPreview = calculatePreview();

      if (freshPreview.points > storedDiscount.points && freshPreview.points >= CONFIG.MIN_DISCOUNT) {
        log('Stale discount detected: stored=' + storedDiscount.points + 'AD, current balance allows=' + freshPreview.points + 'AD — auto-upgrading');
        await handleRemoveDiscount(storedDiscount.code, true);
        // handleRemoveDiscount will call updateCartUI again via its setTimeout — stop here
        return;
      }

      // Stored discount is still the correct amount
      if (statusContainer) {
        statusContainer.innerHTML = '<div style="margin-top:6px;padding:8px 14px;background:#fff;border:0.5px solid #E0DDD8;text-align:center;"><span style="font-size:10px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;color:#9BB8B1;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">✓ Discount Applied</span></div>';
      }
      log('Stored discount is optimal — using Shopify native totals');
      clearPreviewFromCart();

    } else if (preview.points > 0 && preview.dollars > 0) {
      // No stored discount — show preview
      log('No stored discount — showing preview');
      if (statusContainer) statusContainer.innerHTML = '';
      applyPreviewToCart(preview);

    } else {
      log('No discount available — clearing UI');
      if (statusContainer) statusContainer.innerHTML = '';
      clearPreviewFromCart();
    }

    log('Preview: ' + preview.points + 'AD = -$' + preview.dollars.toFixed(2) + ' (eligible: $' + preview.eligible.toFixed(2) + ')');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PREVIEW VISUALS
  // ═══════════════════════════════════════════════════════════════════════════

  function applyPreviewToCart(preview) {
    const items = document.querySelectorAll('[data-product-tags]');
    const discountCents = pointsToCents(preview.points);
    const eligibleCents = getEligibleTotal();
    if (eligibleCents === 0 || discountCents === 0) return;

    const discountRatio = discountCents / eligibleCents;

    items.forEach(item => {
      const tags = item.dataset.productTags || '';
      if (!tags.toLowerCase().includes('nexchange')) return;

      const priceStr = item.dataset.originalLinePrice || '0';
      const originalCents = parseInt(priceStr, 10) || 0;
      const itemDiscountCents = Math.round(originalCents * discountRatio);
      const newPriceCents = originalCents - itemDiscountCents;

      const priceWrapper = item.querySelector('.cart-item__price-wrapper');
      if (priceWrapper) {
        const priceEl = priceWrapper.querySelector('.price');
        if (priceEl && !priceEl.classList.contains('aldwyn-modified')) {
          if (!priceEl.dataset.aldwynOriginal) priceEl.dataset.aldwynOriginal = priceEl.textContent;
          priceEl.classList.add('aldwyn-modified');
          priceEl.innerHTML = '<div style="display:flex;flex-direction:column;align-items:flex-end;"><span style="text-decoration:line-through;color:#9ca3af;font-size:0.75em;line-height:1.2;">' + formatMoney(originalCents) + '</span><span style="color:#059669;font-weight:700;font-size:0.875em;line-height:1.2;">' + formatMoney(newPriceCents) + '</span></div>';
        }
      }
    });

    const totalsContainer = document.querySelector('.totals[role="status"]');
    if (totalsContainer) {
      totalsContainer.querySelector('.aldwyn-discount-line')?.remove();
      totalsContainer.querySelector('.aldwyn-discounted-total')?.remove();

      const totalHeading = totalsContainer.querySelector('.totals__total');
      const totalValue = totalsContainer.querySelector('.totals__total-value');

      if (totalHeading && totalValue) {
        const totalMatch = (totalValue.textContent || '').match(/\$?([\d,]+\.\d{2})/);
        if (totalMatch) {
          const currentTotal = parseFloat(totalMatch[1].replace(/,/g, ''));
          const newTotal = Math.max(0, currentTotal - (discountCents / 100));

          totalHeading.style.display = 'none';
          totalValue.style.display = 'none';

          const customTotals = document.createElement('div');
          customTotals.className = 'aldwyn-discount-line';
          customTotals.style.cssText = 'width:100%;padding:14px 18px;background:#fff;border-top:0.5px solid #E0DDD8;margin:12px 0 0;';
          customTotals.innerHTML = '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;"><span style="font-size:13px;font-weight:400;color:#777;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">Subtotal</span><span style="font-size:13px;font-weight:400;color:#777;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">$' + currentTotal.toFixed(2) + '</span></div><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;"><span style="font-size:13px;font-weight:600;color:#9BB8B1;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">Aldwyn Dollars</span><span style="font-size:13px;font-weight:600;color:#9BB8B1;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">-' + formatMoney(discountCents) + '</span></div><div style="display:flex;justify-content:space-between;align-items:center;padding-top:10px;border-top:0.5px solid #E0DDD8;"><span style="font-size:13px;font-weight:700;color:#37352F;letter-spacing:0.06em;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">Estimated Total</span><span style="font-size:13px;font-weight:700;color:#37352F;font-family:var(--font-heading-family,\'Montserrat\',sans-serif);">$' + newTotal.toFixed(2) + '</span></div>';

          totalValue.parentNode.insertBefore(customTotals, totalValue.nextSibling);
        }
      }
    }
  }

  function clearPreviewFromCart() {
    document.querySelectorAll('.price.aldwyn-modified').forEach(priceEl => {
      if (priceEl.dataset.aldwynOriginal) {
        priceEl.textContent = priceEl.dataset.aldwynOriginal;
        delete priceEl.dataset.aldwynOriginal;
      }
      priceEl.classList.remove('aldwyn-modified');
    });

    const totalHeading = document.querySelector('.totals__total');
    const totalValue = document.querySelector('.totals__total-value');
    if (totalHeading) totalHeading.style.display = '';
    if (totalValue) totalValue.style.display = '';

    const totalsContainer = document.querySelector('.totals[role="status"]');
    if (totalsContainer) {
      totalsContainer.querySelector('.aldwyn-discount-line')?.remove();
      totalsContainer.querySelector('.aldwyn-discounted-total')?.remove();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // API CALLS
  // ═══════════════════════════════════════════════════════════════════════════

  async function apiCreateDiscount(points) {
    const customer = getCustomer();
    log('API: Creating discount for ' + points + ' points');
    const response = await fetch(CONFIG.API_BASE + '/create-discount', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerEmail: customer.email, customerId: customer.id, points })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'HTTP ' + response.status);
    return data;
  }

  async function apiRemoveDiscount(code) {
    const customer = getCustomer();
    log('API: Removing discount code ' + code);
    const response = await fetch(CONFIG.API_BASE + '/remove-discount', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerEmail: customer.email, customerId: customer.id, code })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'HTTP ' + response.status);
    return data;
  }

  async function apiCheckActiveDiscount() {
    const customer = getCustomer();
    if (!customer) return null;
    try {
      log('API: Checking for active discount...');
      const response = await fetch(CONFIG.API_BASE + '/check-discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerEmail: customer.email, customerId: customer.id })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return null;
      if (data.success && data.hasActiveDiscount && data.code) {
        log('API: Found active discount - ' + data.code + ' (' + data.points + 'AD)');
        return { code: data.code, points: data.points, timestamp: Date.now() };
      }
      log('API: No active discount found');
      return null;
    } catch (error) {
      log('API: check-discount error - ' + error.message);
      return null;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REMOVE DISCOUNT
  // silent = true: no UI prompts (used for auto-refund and stale-code upgrade)
  // ═══════════════════════════════════════════════════════════════════════════

  async function handleRemoveDiscount(code, silent = false) {
    if (state.isRemoving) { log('Already removing, ignoring duplicate call'); return; }
    state.isRemoving = true;

    const removeBtn = document.getElementById('aldwyn-remove-discount-btn');
    if (removeBtn && !silent) {
      removeBtn.disabled = true;
      removeBtn.style.opacity = '0.5';
      removeBtn.style.cursor = 'not-allowed';
      removeBtn.textContent = 'Removing...';
    }

    const notif = silent ? null : showNotification('working', 'Removing discount...', 0);

    try {
      const result = await apiRemoveDiscount(code);
      if (!result.success) throw new Error(result.error || 'Failed to remove discount');

      log('Clearing Shopify cart discount...');

      if (!silent) {
        try {
          await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { 'discount': '' } }) });
          await fetch(window.location.origin + '/discount/CLEAR');
          await fetch(window.location.origin + '/cart?discount=', { method: 'GET', credentials: 'same-origin' });
        } catch (cartError) { log('Cart discount clear warning:', cartError.message); }
      } else {
        try {
          await fetch(window.location.origin + '/cart?discount=', { method: 'GET', credentials: 'same-origin' });
          await fetch(window.location.origin + '/discount/CLEAR');

          log('Verifying discount removal...');
          let verified = false;
          for (let i = 0; i < 5; i++) {
            const cartResp = await fetch('/cart.js');
            if (cartResp.ok) {
              const cartData = await cartResp.json();
              const hasDiscount = cartData.cart_level_discount_applications?.length > 0 || cartData.discount_applications?.length > 0 || (cartData.attributes && cartData.attributes.discount);
              if (!hasDiscount) { log('✓ Discount cleared after ' + (i + 1) + ' attempt(s)'); verified = true; break; }
              await new Promise(resolve => setTimeout(resolve, 100));
            } else { break; }
          }
          if (!verified) log('Warning: Could not verify discount removal, continuing anyway');

          fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { 'discount': '' } }) }).catch(() => {});
        } catch (clearError) { log('Silent clearing warning:', clearError.message); }
      }

      clearStoredDiscount();
      localStorage.removeItem('aldwyn_checkout_code');
      localStorage.removeItem('aldwyn_checkout_time');
      localStorage.removeItem('aldwyn_checkout_points');
      localStorage.setItem('aldwyn_just_removed', Date.now().toString());
      log('Set removal flag to prevent re-application on reload');

      if (result.newBalance !== undefined) window.ALDWYN.balance = result.newBalance;

      const refunded = result.refundedPoints || 0;
      log('✓ Discount removed, refunded ' + refunded + 'AD');

      clearPreviewFromCart();

      const customer = getCustomer();
      if (customer) {
        const balanceEl = document.getElementById('CartDrawer-AldwynBalance');
        const usdEl = document.getElementById('CartDrawer-AldwynUSD');
        if (balanceEl) balanceEl.textContent = window.ALDWYN.balance;
        if (usdEl) usdEl.textContent = (window.ALDWYN.balance / CONFIG.POINTS_PER_DOLLAR).toFixed(2);
      }

      const statusContainer = document.getElementById('aldwyn-status-container');
      if (statusContainer) statusContainer.innerHTML = '';

      document.querySelectorAll('.discounts__discount, .cart-item__discounted-prices').forEach(el => { log('Removing Shopify discount element from DOM'); el.remove(); });

      if (silent) {
        log('Forcing cart drawer refresh...');
        try {
          const cartResp = await fetch(window.location.pathname + '?section_id=cart-drawer', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (cartResp.ok) {
            const html = await cartResp.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newItems = doc.querySelector('cart-drawer-items');
            const oldItems = document.querySelector('cart-drawer-items');
            if (oldItems && newItems) { oldItems.innerHTML = newItems.innerHTML; log('✓ Cart HTML refreshed'); }
          }
        } catch (refreshError) { log('Cart refresh warning:', refreshError.message); }

        setTimeout(async () => { log('Re-applying Aldwyn UI after refresh...'); await updateCartUI(); }, 200);
      }

      if (!silent) {
        if (notif) notif.remove();
        showNotification('success', refunded + 'AD restored!', 2000);
      } else {
        showNotification('info', refunded + 'AD auto-refunded', 2000);
      }

      state.isRemoving = false;

    } catch (error) {
      log('❌ Remove discount error:', error.message);
      if (removeBtn && !silent) {
        removeBtn.disabled = false;
        removeBtn.style.opacity = '1';
        removeBtn.style.cursor = 'pointer';
        removeBtn.textContent = 'Remove';
      }
      if (!silent) {
        if (notif) notif.remove();
        showNotification('error', 'Failed to remove: ' + error.message, 4000);
      }
      state.isRemoving = false;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CHECKOUT INTERCEPT
  // ═══════════════════════════════════════════════════════════════════════════

  async function handleCheckoutClick(e) {
    e.preventDefault();

    const storedDiscount = await getStoredDiscount();
    if (storedDiscount) {
      log('Using existing discount: ' + storedDiscount.code);
      window.location.href = '/checkout?discount=' + encodeURIComponent(storedDiscount.code);
      return;
    }

    const preview = calculatePreview();
    if (preview.points === 0) { window.location.href = '/checkout'; return; }
    if (state.isApplying) return;

    state.isApplying = true;
    const notif = showNotification('working', 'Applying discount...', 0);

    try {
      log('Applying ' + preview.points + 'AD before checkout...');
      const result = await apiCreateDiscount(preview.points);
      if (!result.success) throw new Error(result.error || 'Failed to create discount');

      const code = result.code || result.discountCode;
      if (!code) throw new Error('No discount code returned');

      saveDiscount(code, preview.points);
      localStorage.setItem('aldwyn_checkout_code', code);
      localStorage.setItem('aldwyn_checkout_time', Date.now().toString());
      localStorage.setItem('aldwyn_checkout_points', preview.points.toString());

      if (result.newBalance !== undefined) window.ALDWYN.balance = result.newBalance;

      log('✓ Discount applied: ' + code + ' (' + preview.points + 'AD)');
      notif.remove();
      showNotification('success', 'Saving ' + formatMoney(pointsToCents(preview.points)) + '!', 1500);

      setTimeout(() => { window.location.href = '/checkout?discount=' + encodeURIComponent(code); }, 500);

    } catch (error) {
      log('Checkout apply error:', error.message);
      notif.remove();
      showNotification('error', 'Failed: ' + error.message, 3000);
      setTimeout(() => { state.isApplying = false; window.location.href = '/checkout'; }, 1500);
    }
  }

  function setupCheckoutIntercept() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[name="checkout"], .cart__checkout-button, #CartDrawer-Checkout');
      if (btn) handleCheckoutClick(e);
    }, true);

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && (form.action.includes('/checkout') || form.id?.includes('checkout'))) handleCheckoutClick(e);
    }, true);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO RECOVERY - Refund points if checkout was abandoned
  // ═══════════════════════════════════════════════════════════════════════════

  async function checkAndRecoverPoints() {
    const code = localStorage.getItem('aldwyn_checkout_code');
    const timeStr = localStorage.getItem('aldwyn_checkout_time');
    const pointsStr = localStorage.getItem('aldwyn_checkout_points');
    if (!code || !timeStr || !pointsStr) return;

    log('Checking if checkout discount ' + code + ' still exists...');
    const activeDiscount = await apiCheckActiveDiscount();

    if (!activeDiscount || activeDiscount.code !== code) {
      log('Checkout discount already removed — clearing localStorage');
      localStorage.removeItem('aldwyn_checkout_code');
      localStorage.removeItem('aldwyn_checkout_time');
      localStorage.removeItem('aldwyn_checkout_points');
      return;
    }

    log('Auto-recovering ' + pointsStr + 'AD — user returned from checkout without completing purchase');
    localStorage.removeItem('aldwyn_checkout_code');
    localStorage.removeItem('aldwyn_checkout_time');
    localStorage.removeItem('aldwyn_checkout_points');
    await handleRemoveDiscount(code, true);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CART MONITORING
  // ═══════════════════════════════════════════════════════════════════════════

  let updateTimer = null;
  function onCartChange() {
    if (updateTimer) clearTimeout(updateTimer);
    updateTimer = setTimeout(() => { updateCartUI(); }, 300);
  }

  function setupCartMonitoring() {
    if (typeof window.subscribe === 'function') window.subscribe('cart-update', onCartChange);
    else if (typeof subscribe === 'function') subscribe('cart-update', onCartChange);

    const drawer = document.querySelector('cart-drawer-items, cart-items, .cart-drawer__inner');
    if (drawer) new MutationObserver(() => onCartChange()).observe(drawer, { childList: true, subtree: true });

    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="updates[]"], .quantity__input')) onCartChange();
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  function init() {
    const customer = getCustomer();
    setupCartMonitoring();

    if (!customer?.email) {
      log('No customer logged in — showing login message');
      setTimeout(() => updateCartUI(), 500);
      setTimeout(() => updateCartUI(), 1500);
      document.addEventListener('click', (e) => {
        if (e.target.closest('[aria-controls*="cart"], .cart-icon, .header__icon--cart')) {
          setTimeout(() => updateCartUI(), 100);
          setTimeout(() => updateCartUI(), 500);
        }
      });
      return;
    }

    log('Init: ' + customer.email + ', Balance: ' + customer.balance + 'AD');

    checkAndRecoverPoints();
    setupCheckoutIntercept();

    setTimeout(() => updateCartUI(), 500);
    setTimeout(() => updateCartUI(), 1500);
    setTimeout(() => updateCartUI(), 3000);

    document.addEventListener('click', (e) => {
      if (e.target.closest('[aria-controls*="cart"], .cart-icon, .header__icon--cart')) {
        setTimeout(() => updateCartUI(), 100);
        setTimeout(() => updateCartUI(), 500);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  setTimeout(init, 1500);

})();